import { Button } from "../Button/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Tabs } from "../Tabs/Tabs";
import { Tooltip } from "../Tooltip/Tooltip";
import {
  ArrowBack as ArrowLeftOutlined,
  SettingsOutlined as SettingOutlined,
} from "@mui/icons-material";
import { useRangerChatStore } from "./chatStore";
import { useOptionalCurrentUser } from "../../utils/hooks";
import { MemoryTab } from "./menu/MemoryTab";
import { ConversationHistoryTab } from "./menu/ConversationHistoryTab";
import { PromptsTab } from "./menu/PromptsTab";
import { ToolsTab } from "./menu/ToolsTab";
import { SkillsTab } from "./menu/SkillsTab";
import { AgentsTab } from "./menu/AgentsTab";
import { NotificationsTab } from "./menu/NotificationsTab";
import { ChatToolType } from "_graphql-types-frontend/graphql";

/** Wide enough for the split-view version diff (the old modal was 960). */
const SETTINGS_MAX_WIDTH = 1100;

type ToolDisplay = {
  tool: ChatToolType;
  name: string;
  description: string;
};

/**
 * The gear. Toggles the settings VIEW rather than opening an overlay.
 *
 * Settings used to be a popover (a drawer on mobile) that then launched modals
 * up to 960px wide from inside a 640px container — six of them across the tabs,
 * each escaping its own parent via a portal and covering the rest of the app.
 * Configuring a chat is not something that should obscure everything around it:
 * it is about THIS chat, so it happens in this chat's own space, and a back
 * button returns you to the conversation.
 */
export function RangerChatMenuButton() {
  const menuOpen = useRangerChatStore(s => s.menuOpen);
  const setMenuOpen = useRangerChatStore(s => s.setMenuOpen);

  return (
    <Tooltip title={menuOpen ? "Back to chat" : "Chat Settings"}>
      <Button
        shape="circle"
        icon={menuOpen ? <ArrowLeftOutlined /> : <SettingOutlined />}
        data-cy="ranger-chat-menu-button"
        aria-label={menuOpen ? "Back to chat" : "Chat settings"}
        onClick={() => setMenuOpen(!menuOpen)}
      />
    </Tooltip>
  );
}

/**
 * Chat settings, rendered in place of the conversation.
 *
 * Takes whatever width the host gives the chat panel, so it is roomy in
 * fullscreen and workable in the sidebar — and it never covers the rest of the
 * application, which a 960px modal launched from a sidebar popover did.
 */
export function RangerChatSettings({
  availableToolsDisplay,
}: {
  availableToolsDisplay: ToolDisplay[];
}) {
  const setMenuOpen = useRangerChatStore(s => s.setMenuOpen);
  const menuActiveTab = useRangerChatStore(s => s.menuActiveTab);
  const setMenuActiveTab = useRangerChatStore(s => s.setMenuActiveTab);

  // Configured agents are a subscriber feature. The server gates read, spawn
  // and write alike (canUseAgents), so a non-subscriber opening this tab would
  // only ever see an empty list — hide it rather than advertise a dead end.
  // useOptionalCurrentUser, not the throwing variant: a transient empty user
  // cache must degrade, not crash chat.
  const currentUser = useOptionalCurrentUser();
  const canUseAgents =
    !!currentUser?.flags?.isSuperUser ||
    (!!currentUser?.flags?.coreUser && !!currentUser?.flags?.isSubscriptionUser);

  // The scroll container. Every ancestor up to the panel must participate or
  // this never bounds: `overflow: auto` on an auto-height box does nothing, it
  // just overflows — which is what broke scrolling in Tools when the old fixed
  // `min(70vh, 480px)` height was removed. The chain is:
  //   settings root (flex column, height 100%)
  //     -> Tabs outer  (flex 1, min-height 0, flex column)  via `style`
  //     -> Tabs content(flex 1, min-height 0, flex column)  via `contentStyle`
  //     -> this pane   (flex 1, min-height 0, overflow auto)
  const tabPane = (children: React.ReactNode) => (
    <div
      style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      data-testid="settings-scroll-pane"
    >
      {children}
    </div>
  );

  const fillColumn = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  } as const;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: 12,
        boxSizing: "border-box",
        // Fullscreen hands this the whole window now that the conversation
        // rail is hidden, and a directive textarea stretched across 1600px is
        // unreadable. Capped and centred — but at 1100 rather than a typical
        // prose measure, because the version diff is a two-column split view
        // and used to get 960px as a modal. It should not come out of this
        // change worse off.
        width: "100%",
        maxWidth: SETTINGS_MAX_WIDTH,
        marginInline: "auto",
      }}
      data-testid="ranger-chat-settings"
    >
      {/* The panel replaces the conversation, so it says what it is: a
          titled header, with the way back beside the title rather than a
          separate text button above it. */}
      <Box
        component="header"
        data-testid="settings-header"
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexShrink: 0 }}
      >
        <Tooltip title="Back to chat">
          <IconButton
            size="small"
            aria-label="Back to chat"
            onClick={() => setMenuOpen(false)}
            data-testid="settings-back-button"
          >
            <ArrowLeftOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.25 }}>
            Settings
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Customize how Sirius AI works for you.
          </Typography>
        </Box>
      </Box>

      <Tabs
        activeKey={menuActiveTab}
        onChange={setMenuActiveTab}
        size="small"
        style={fillColumn}
        contentStyle={fillColumn}
        items={[
          { key: "memory", label: "Memory", children: tabPane(<MemoryTab />) },
          {
            key: "history",
            label: "History",
            children: tabPane(<ConversationHistoryTab />),
          },
          { key: "prompts", label: "Prompts", children: tabPane(<PromptsTab />) },
          {
            key: "tools",
            label: "Tools",
            children: tabPane(
              <ToolsTab availableToolsDisplay={availableToolsDisplay} />
            ),
          },
          { key: "skills", label: "Skills", children: tabPane(<SkillsTab />) },
          ...(canUseAgents
            ? [
                {
                  key: "agents",
                  label: "Agents",
                  children: tabPane(<AgentsTab />),
                },
              ]
            : []),
          {
            key: "notifications",
            label: "Notifications",
            children: tabPane(<NotificationsTab />),
          },
        ]}
      />
    </div>
  );
}

/**
 * Back-compat shim: the button plus, when open, the settings body inline.
 *
 * `RangerChat` renders the two separately (button in the composer's control
 * row, body in place of the transcript). This keeps a single-component entry
 * point for anything that mounts the menu on its own.
 */
export function RangerChatMenu({
  availableToolsDisplay,
}: {
  availableToolsDisplay: ToolDisplay[];
}) {
  const menuOpen = useRangerChatStore(s => s.menuOpen);
  return (
    <>
      <RangerChatMenuButton />
      {menuOpen && (
        <RangerChatSettings availableToolsDisplay={availableToolsDisplay} />
      )}
    </>
  );
}
